import { Suspense, useCallback, useEffect, useState } from "react";
import { systemWidth, useTheme } from "@kaltura/mediaspace-shared-styled";
import { baseUrl, fetchKmsData, translate, useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import PlaylistsCarouselsSkeleton from "./PlaylistsCarouselsSkeleton";
import { EventPlatformPlaylist, PlaylistsResponse } from "./PlaylistCarousels";
import styled from "@emotion/styled";
import { NoResults } from "@kaltura/mediaspace-shared-search";
import { Button } from "@kaltura/ds-react-components";
import { PlaylistsList } from "./PlaylistsList";
import { DataLoader } from "../DataLoader";
import { FiltersValues } from "@kaltura/mediaspace-shared-search-with-filter-tags";

export interface PlaylistsContainerProps {
    categoryId?: number;
    initialPlaylists: EventPlatformPlaylist[];
    hasMorePlaylists: boolean;
    searchKeyword?: string;
    searchByTags?: FiltersValues;
    showNoResults?: boolean;
    onNoResults?: () => void;
}

const ButtonContainer = styled("div")(({ theme }) => ({
    "&": systemWidth({ theme }),
    display: "flex",
    justifyContent: "center",
}));

/**
 * PlaylistsContainer component
 *
 * This component is responsible for displaying a list of playlists and handling the loading of additional playlists.
 * It supports both initial loading and loading more playlists on demand, with support for search functionality.
 *
 * Props:
 * - categoryId: The ID of the category to which the playlists belong.
 * - initialPlaylists: The initial list of playlists to display.
 * - hasMorePlaylists: A boolean indicating if there are more playlists to load.
 * - searchKeyword: The keyword to filter playlists by.
 * - searchByTags: An array of tags to filter playlists by.
 * - showNoResults: A boolean indicating if a "No Results" message should be shown when there are no playlists search results.
 */
export const PlaylistsContainer = ({
    categoryId,
    initialPlaylists,
    hasMorePlaylists,
    searchKeyword,
    searchByTags,
    showNoResults = true,
    onNoResults,
}: PlaylistsContainerProps) => {
    // Number of pages already loaded (initial data is considered the first page).
    const [pagesLoaded, setPagesLoaded] = useState(1);
    // Number of search pages already loaded.
    const [searchPagesLoaded, setSearchPagesLoaded] = useState(0);
    // All loaded playlists (not in the context of search).
    // we would like to present it immediately when search keyword is cleared.
    const [allLoadedPlaylists, setAllLoadedPlaylists] = useState<EventPlatformPlaylist[]>(initialPlaylists);
    // Whether there are more playlists to load in the "all" context.
    // we would like to present it immediately when search keyword is cleared.
    const [showAllHasMore, setShowAllHasMore] = useState(hasMorePlaylists);
    // Whether there are more playlists to load in the search context.
    const [showSearchHasMore, setShowSearchHasMore] = useState(false);
    // Playlists to show (either all or filtered by search).
    const [playlistsToShow, setPlaylistsToShow] = useState<EventPlatformPlaylist[]>(initialPlaylists);
    // Resource function for fetching more playlists.
    const [resource, setResource] = useState<() => PlaylistsResponse>(); // Only for dynamic data
    // Number of lines to render in the loading skeleton.
    const [skeletonNumberOfLines, setSkeletonNumberOfLines] = useState(1);

    const theme = useTheme();
    const isSmScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

    // Function to load more playlists when the "Load more" button is clicked.
    const loadMore = () => {
        setSkeletonNumberOfLines(1);
        const isSearch = !!searchKeyword || searchByTags;
        isSearch ? setShowSearchHasMore(false) : setShowAllHasMore(false);
        const url = isSearch
            ? `${baseUrl}/channelthemingui/index/search-playlists`
            : `${baseUrl}/channelthemingui/index/get-playlists?page=${pagesLoaded + 1}&categoryId=${categoryId}`;
        const data = isSearch
            ? { categoryId, page: searchPagesLoaded + 1, keyword: searchKeyword, tagsFilter: searchByTags }
            : undefined;
        setResource(() => fetchKmsData<PlaylistsResponse>(url, data, true));
    };

    // Callback function to handle the loaded page of playlists.
    const onPageLoaded = (data: PlaylistsResponse) => {
        if (data.error) {
            console.error("Error loading more playlists: " + data.error);
            return;
        }
        if (searchKeyword) {
            setPlaylistsToShow((prevItems) => {
                const playlistEntries = [...prevItems, ...data.playlists];
                if (playlistEntries.length === 0) {
                    onNoResults?.(); // Call the onNoResults callback if there are no playlists.
                }
                return playlistEntries;
            });
            setSearchPagesLoaded((prevPages) => prevPages + 1); // Increment the search pages loaded counter.
            setShowSearchHasMore(data.morePlaylists); // Update the "has more" state for search context.
        }
        else {
            setAllLoadedPlaylists((prevItems) => [...prevItems, ...data.playlists]); // Append new playlists to the existing list.
            setPlaylistsToShow((prevItems) => [...prevItems, ...data.playlists]); // Update the playlists to show.
            setPagesLoaded((prevPages) => prevPages + 1); // Increment the pages loaded counter.
            setShowAllHasMore(data.morePlaylists); // Update the "has more" state for all context.
        }
    };

    const initialPlaylistsSearch = useCallback(() => {
        if (searchKeyword || (searchByTags && Object.keys(searchByTags).length > 0)) {
            const url = `${baseUrl}/channelthemingui/index/search-playlists`;
            // this is the initial search, load more will handle the rest
            return fetchKmsData<PlaylistsResponse>(
                url,
                {
                    categoryId,
                    page: 1,
                    keyword: searchKeyword,
                    tagsFilter: searchByTags ? Object.entries(searchByTags).map(([param, values]) => values) : [],
                },
                true
            );
        }
    }, [categoryId, searchKeyword, searchByTags]);

    // Effect to handle changes in the search keyword.
    useEffect(() => {
        if (searchKeyword || searchByTags) {
            // reset pages counter when search keyword changes
            setSearchPagesLoaded(0);
            setSkeletonNumberOfLines(3);
            setPlaylistsToShow([]);
            if (initialPlaylistsSearch) {
                setResource(initialPlaylistsSearch);
            }
        }
        else {
            setPlaylistsToShow(allLoadedPlaylists);
            setSkeletonNumberOfLines(1);
            setShowSearchHasMore(false);
        }
    }, [searchKeyword, searchByTags, initialPlaylistsSearch]);

    // Loading skeleton component to show while playlists are loading.
    const loadingSkeleton = (
        <PlaylistsCarouselsSkeleton numberOfLines={skeletonNumberOfLines} fullScreenWidth={isSmScreen} />
    );

    // Determine if the "Load more playlists" button should be shown.
    const shouldShowLoadMoreButton =
        (!searchKeyword && !searchByTags && showAllHasMore) || ((searchKeyword || searchByTags) && showSearchHasMore);

    return (
        <>
            {/* PlaylistsList component to display the list of playlists, at first it will show the initial playlists which are loaded on component first render */}
            <PlaylistsList data={playlistsToShow} categoryId={categoryId} />
            {/* Suspense component to handle the loading state of PlaylistsLoader */}
            {resource && (
                <Suspense fallback={loadingSkeleton}>
                    {/* PlaylistsLoader component to load more playlists dynamically */}
                    <DataLoader fetchKmsData={resource} onLoaded={onPageLoaded} />
                    {playlistsToShow.length === 0 && showNoResults && <NoResults screenMode={"nomatches"} />}
                </Suspense>
            )}
            {shouldShowLoadMoreButton && (
                <ButtonContainer>
                    <Button color={"secondary"} onClick={loadMore}>
                        {translate("Load more playlists")}
                    </Button>
                </ButtonContainer>
            )}
        </>
    );
};
